import { memo } from 'react'
import { makeEventTrack, sendEvent } from 'src/utils/restructure/analytics'

import type { ProductItem } from '../../types/productItem'
import type { Product } from '../../types/product'
import type { Review } from '../../types/review'
import { Resumer } from './Resumer'
import { ReviewsList } from './ReviewsList'
import { useReviews } from './useReviews'
import LoadMoreButton from './LoadMoreButton'

import './styles.scss'

function CustomerReviews({
  currentSku,
  product,
  rating,
}: {
  currentSku: ProductItem
  product: Product
  rating?: Review | null
}) {
  const manufacturerCode = currentSku?.manufacturerCode
  const sellerId = currentSku?.sellerDefault?.sellerId
  const { review: productReview } = product

  const review = productReview ?? rating

  const decathlonSellerId = '1'
  const sellerIsDecathlon = sellerId === decathlonSellerId

  const notesConvertedToKeyValue = Object.entries(review?.notes ?? {}).map(
    (item) => {
      const mapKey: { [key: string]: number } = {
        one: 1,
        two: 2,
        three: 3,
        four: 4,
        five: 5,
      }

      return {
        ...item[1],
        key: mapKey[item[0]],
      }
    }
  )

  const { reviews, handleGetMoreReviews, currentPage, loading } = useReviews({
    initialReviews: review?.reviews ?? [],
    totalReviews: review?.totalReviews ?? 0,
    productId: product.productId,
  })

  const shouldShowSeeAllReviewsButton =
    review?.totalReviews &&
    review.totalReviews > 3 &&
    !currentPage.includes((review.totalReviews - 1).toString())

  const reviewEventTrack = makeEventTrack({
    eventAction: 'ver todas as avaliações',
    eventPage: 'pagina de produto',
  })

  if (!sellerIsDecathlon) {
    return <></>
  }

  return (
    <section id="customer_reviews_section" data-testid="customerReviews">
      <div className="max-w-[1280px] m-auto px-md">
        <div className="gap-6 flex flex-col">
          <Resumer
            totalReviews={review?.totalReviews}
            ratingNote={review?.totalRatingsAverageNote}
            notes={notesConvertedToKeyValue}
            productCode={manufacturerCode}
          />

          {!!review?.totalReviews && (
            <div className="flex-1" data-testid="reviewsList">
              <ReviewsList reviewList={reviews} />

              {shouldShowSeeAllReviewsButton && (
                <LoadMoreButton
                  onClick={() => {
                    handleGetMoreReviews()
                    sendEvent(reviewEventTrack)
                  }}
                  loading={loading}
                  testId="seeAllReviewsButtonMobile"
                  ariaLabel="Botão para carregar mais avaliações"
                  isMobile
                />
              )}
            </div>
          )}

          <div className="flex flex-row justify-between w-full">
            {shouldShowSeeAllReviewsButton && (
              <LoadMoreButton
                onClick={() => {
                  handleGetMoreReviews()
                  sendEvent(reviewEventTrack)
                }}
                loading={loading}
                testId="seeAllReviewsBtn"
                ariaLabel="Botão para carregar mais avaliações"
                isMobile={false}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default memo(CustomerReviews)
